'use client';

import Script from 'next/script';
import { useEffect, useRef } from 'react';
import type { Smartico, SmarticoClientWidgetProps, SmarticoParameter } from './types';

const SmarticoClientWidget = ({
  smarticoId,
  smarticoKey,
  smarticoUrl,
  data,
  locale,
  isLocalhost,
}: SmarticoClientWidgetProps) => {
  const isInitialized = useRef(false);
  const firebaseAppLoaded = useRef(false);
  const firebaseMessagingLoaded = useRef(false);
  const smarticoMessagingLoaded = useRef(false);
  const externalScriptLoaded = useRef(false);

  useEffect(() => {
    // Prevent multiple initializations
    if (isInitialized.current) {
      return;
    }

    console.log('Initializing SmarticoClientWidget ...');

    const initializeSmartico = () => {
      // Define Smartico function
      const smartico: Smartico = function (command: string, parameter?: SmarticoParameter) {
        if (smartico.q) {
          smartico.q.push([command, parameter]);
        } else {
          smartico.q = [];
          smartico.q.push([command, parameter]);
        }
      };

      // Set up Smartico settings
      window._smartico_user_id = data?.id?.toString() ?? null;
      window._smartico_language = locale ?? 'en';

      // Only allow localhost if actually running on development/preview
      window._smartico_allow_localhost = isLocalhost;

      window._smartico = smartico;
      isInitialized.current = true;
    };

    // Initialize immediately
    initializeSmartico();
  }, [data, locale, smarticoId, smarticoKey]);

  useEffect(() => {
    if (!data?.id) {
      window._smartico_user_id = null;
      window._smartico_language = null;
    }
  }, [data?.id]);

  const handleFirebaseAppLoad = () => {
    console.log('Head Tags :: Smartico Firebase App script loaded successfully');
    firebaseAppLoaded.current = true;
  };

  const handleFirebaseMessagingLoad = () => {
    console.log('Head Tags :: Smartico Firebase Messaging script loaded successfully');
    firebaseMessagingLoaded.current = true;
  };

  const handleSmarticoMessagingLoad = () => {
    console.log('Head Tags :: Smartico Messaging script loaded successfully');
    smarticoMessagingLoaded.current = true;
  };

  const handleExternalScriptLoad = () => {
    console.log('Head Tags :: Smartico external script loaded successfully');
    externalScriptLoaded.current = true;

    // Initialize Smartico after external script loads
    if (window._smartico?.init && smarticoId && smarticoKey) {
      window._smartico.init(smarticoId, { brand_key: smarticoKey });
    }
  };

  const handleScriptError = (scriptName: string) => {
    console.error(`Head Tags :: Failed to load ${scriptName} script`);
  };

  if (!smarticoUrl) {
    return null;
  }

  return (
    <>
      {/* Load Firebase App script */}
      <Script
        src="https://d312ucx3huj7iy.cloudfront.net/firebase-app.js"
        // strategy="afterInteractive"
        strategy="lazyOnload"
        onLoad={handleFirebaseAppLoad}
        onError={() => handleScriptError('Firebase App')}
      />

      {/* Load Firebase Messaging script */}
      <Script
        src="https://d312ucx3huj7iy.cloudfront.net/firebase-messaging.js"
        // strategy="afterInteractive"
        strategy="lazyOnload"
        onLoad={handleFirebaseMessagingLoad}
        onError={() => handleScriptError('Firebase Messaging')}
      />

      {/* Load Smartico Messaging script */}
      {/* TODO: Enable when Firebase credentials are ready */}
      <Script
        src="https://d312ucx3huj7iy.cloudfront.net/smartico-messaging-core-v2.js"
        strategy="afterInteractive"
        onLoad={handleSmarticoMessagingLoad}
        onError={() => handleScriptError('Smartico Messaging')}
      />

      {/* Load the external Smartico script */}
      <Script
        src={smarticoUrl}
        // strategy="afterInteractive"
        strategy="lazyOnload"
        onLoad={handleExternalScriptLoad}
        onError={() => handleScriptError('Smartico External')}
      />
    </>
  );
};

export default SmarticoClientWidget;
